import React, {useContext, useEffect} from "react";
import {Controller, useForm} from "react-hook-form";
import {Button, Input, Spacer, SubTitle, Dropdown} from "@dspworkplace/ui";
import { SketchPicker } from 'react-color'
import {toast} from "../../../components/Toasts";
import {upload, useAuth} from "../../../Auth";
import SettingsContext from "../context";
import {AccessCheck, IsGranted} from "../../../security";
import { DropUploader } from "../../../components/DropUploader";
import { calculateContrast } from "../../../utils";
import { Label } from "../../../components/UI";

export default function Information()
{
    const {api} = useAuth();
    const [state, dispatch] = useContext(SettingsContext);
    const {register, handleSubmit, reset, control, setValue} = useForm();
    const isCompany = state.company?.id === 12;

    useEffect(() => {
        reset({
            ...state.company,
            logo: null,
            language: state.companySetting?.language ?? "US",
            ...(isCompany && {
                primaryColor: state.onboardingSetting?.primaryColor || "",
                primaryTextColor: state.onboardingSetting?.primaryTextColor || "",
                secondaryColor: state.onboardingSetting?.secondaryColor || "",
                secondaryTextColor: state.onboardingSetting?.secondaryTextColor || "",
            })
        });
    }, [state.company, state.companySetting, state.onboardingSetting]);

    const handleSaveCompanyInfo = async ( data, evt ) => {
        let form = evt.target;

        form.querySelector('button').disabled = true;
        let logoUrl = null

        if (data?.logo) {
            const formData = new FormData()
            formData.append('image', data?.logo[0]);
            formData.append('name', data?.logo[0]?.name);
            const res = await upload(formData);
            logoUrl = res.data?.ObjectURL
        }

        await api.post('/api/lazy/manage/data', {
            actions: {
                update: {
                    Company: {
                        update_company: {
                            findBy: {
                                id: state.company.id
                            },
                            updateRecord: {
                                name: data.name,
                                amazonAbbreviation: data.amazonAbbreviation,
                                domain: data.domain,
                                logo: logoUrl
                            }
                        }
                    },
                    CompanySetting: {
                        update_company_setting: {
                            findBy: {
                                company: state.company.id
                            },
                            updateRecord: {
                                language: data.language
                            }
                        }
                    },
                    ...(isCompany && {
                        "OnboardingSetting\\OnboardingSetting": {
                            update_onboarding_setting: {
                                findBy: {
                                    company: state.company.id
                                },
                                updateRecord: {
                                    primaryColor: data?.primaryColor,
                                    primaryTextColor: data?.primaryTextColor,
                                    secondaryColor: data?.secondaryColor,
                                    secondaryTextColor: data?.secondaryTextColor
                                }
                            }
                        }
                    })
                }
            }
        }).then(
            response => toast({
                title: 'Company Information updated',
                type: 'info',
                timeout: 8000,
                useIcon: true,
                useClose: true
            }) && dispatch({
                type: 'ADD',
                payload: {
                    ...state,
                    company: {
                        ...state.company,
                        ...data
                    },
                    companySetting: {
                        ...state.companySetting,
                        language: data.language
                    },
                    ...(isCompany) && {
                        onboardingSetting: {
                            ...state.onboardingSetting,
                            primaryColor: data?.primaryColor,
                            primaryTextColor: data?.primaryTextColor,
                            secondaryColor: data?.secondaryColor,
                            secondaryTextColor: data?.secondaryTextColor
                        }
                    }
                }
            }),
            error => toast({
                title: 'Error',
                content: error.response ? error.response.data.message : error.message,
                type: 'error',
                timeout: 8000,
                useIcon: true,
                useClose: true
            })
        ).then(
            ()=> form.querySelector('button').disabled = false
        );
    };

    return (
        <form onSubmit={handleSubmit(handleSaveCompanyInfo)}>
            <input type="hidden" name="primaryTextColor" ref={register} />
            <input type="hidden" name="secondaryTextColor" ref={register} />
            <SubTitle>Company Information</SubTitle>
            <Spacer bottom={3}/>
            <DropUploader
                label='Company Logo'
                name='logo'
                ref={register}
                multiple={false}
                accept={"image/jpeg,image/png"}
                placeholder={[
                    { src: state?.company?.logo, type: "image", id: 1 },
                ]}
            />
            <Spacer bottom={3} />
            {isCompany && (
                <>
                <div>
                    <Label>Primary Color</Label>
                    <Controller
                        name='primaryColor'
                        control={control}
                        as={SketchPicker}
                        valueName='color'
                        defaultValue={state.onboardingSetting?.primaryColor}
                        onChange={([color]) => {
                            const hex = color.hex
                            const isBlack = calculateContrast( hex, '#333333')
                            setValue('primaryTextColor', isBlack ? '#333333' : '#ffffff')
                            return hex
                        }}
                    />
                </div>
                <Spacer bottom={3} />
                <div>
                    <Label>Secondary Color</Label>
                    <Controller
                        name='secondaryColor'
                        control={control}
                        as={SketchPicker}
                        valueName='color'
                        defaultValue={state.onboardingSetting?.secondaryColor}
                        onChange={([color]) => {
                            const hex = color.hex
                            const isBlack = calculateContrast( hex, '#333333')
                            setValue('secondaryTextColor', isBlack ? '#333333' : '#ffffff')
                            return hex
                        }}
                    />
                </div>
                <Spacer bottom={3} />
                </>
            )}
            <Input
                ref={register}
                name='name'
                label='Name'
            />
            <Spacer bottom={3}/>
            <Input
                ref={register}
                name='amazonAbbreviation'
                label='Amazon Abbreviation'
            />
            <Spacer bottom={3}/>
            <Input
                ref={register}
                name='domain'
                label='Domain'
            />
            <Spacer bottom={3}/>
            <Dropdown
                ref={register}
                name="language"
                label="Language"
                options={[
                    { name: "United States (US)", value: "US" },
                    { name: "United Kingdom (UK)", value: "UK" },
                    { name: "European Union (EU)", value: "EU" },
                ]}
            />
            <Spacer bottom={5}/>
            <IsGranted feature={AccessCheck.features.SETTINGS} ops={[AccessCheck.OPS.U]}>
                <Button type='primary'>Save</Button>
            </IsGranted>
        </form>
    )
}